/** Filtre / Select etkileşimlerinde sayfa kaydırmasını korur */

let capturedScrollY = 0;
let hasCapturedScroll = false;

/** Çoklu seçimde menünün seçim sonrası açık kalması (Autocomplete) */
export const FILTER_SELECT_KEEP_OPEN_REASONS = new Set([
  "selectOption",
  "removeOption",
]);

/** MUI Select çoklu seçim — yalnızca bu nedenlerle kapanır */
const FILTER_MENU_DISMISS_REASONS = new Set([
  "backdropClick",
  "escapeKeyDown",
]);

function readScrollY() {
  if (typeof window === "undefined") return 0;
  return window.scrollY || document.documentElement.scrollTop || 0;
}

function writeScrollY(scrollY) {
  if (typeof window === "undefined") return;
  window.scrollTo({ top: scrollY, left: 0, behavior: "auto" });
}

const PAGE_SCROLL_ROOT_SELECTORS = [
  "html",
  "body",
  "#root",
  ".doctor-panel-shell",
  ".tenant-panel-shell",
  ".doctor-panel-shell main",
  ".tenant-panel-shell main",
  "main",
];

/** Route geçişlerinde bilinen kökleri en üste al */
export function scrollPageToTop(behavior = "auto") {
  if (typeof window === "undefined") return;
  window.scrollTo({ top: 0, left: 0, behavior });
  PAGE_SCROLL_ROOT_SELECTORS.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      if (el && typeof el.scrollTop === "number" && el.scrollTop > 0) {
        el.scrollTop = 0;
      }
    });
  });
}

/** SPA'da hangi eleman scroll ediyorsa onu bulup sıfırla */
export function resetAllDocumentScrollPositions(behavior = "auto") {
  if (typeof window === "undefined") return;
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }
  scrollPageToTop(behavior);
  if (!document.body) return;
  const nodes = document.body.querySelectorAll("*");
  for (let i = 0; i < nodes.length; i += 1) {
    const el = nodes[i];
    if (!el || typeof el.scrollTop !== "number" || el.scrollTop <= 0) continue;
    try {
      const { overflowY, overflow } = window.getComputedStyle(el);
      if (
        overflowY === "auto" ||
        overflowY === "scroll" ||
        overflow === "auto" ||
        overflow === "scroll"
      ) {
        el.scrollTop = 0;
      }
    } catch {
      el.scrollTop = 0;
    }
  }
}

/**
 * Layout / lazy içerik / tarayıcı scroll restoration sonrası güvenilir sıfırlama.
 * @param {{ behavior?: ScrollBehavior, delaysMs?: number[] }} [options]
 */
export function forcePageScrollToTop(options = {}) {
  const { behavior = "auto", delaysMs = [0, 50, 150, 350] } = options;
  delaysMs.forEach((delay) => {
    if (delay <= 0) {
      resetAllDocumentScrollPositions(behavior);
    } else {
      setTimeout(() => resetAllDocumentScrollPositions(behavior), delay);
    }
  });
}

/** Render / layout sonrası güvenilir scroll sıfırlama */
export function scrollPageToTopAfterPaint(behavior = "auto") {
  if (typeof window === "undefined") return;
  forcePageScrollToTop({ behavior, delaysMs: [0] });
  requestAnimationFrame(() => {
    requestAnimationFrame(() => forcePageScrollToTop({ behavior, delaysMs: [0] }));
  });
}

export function capturePageScrollY() {
  capturedScrollY = readScrollY();
  hasCapturedScroll = true;
  return capturedScrollY;
}

export function restorePageScrollY(scrollY = capturedScrollY) {
  if (typeof window === "undefined" || !hasCapturedScroll) return;
  const y = scrollY ?? capturedScrollY;
  requestAnimationFrame(() => writeScrollY(y));
}

/** State güncellemesi + yeniden çizim sonrası konumu geri yükle */
export function restorePageScrollAfterPaint(scrollY = capturedScrollY) {
  if (typeof window === "undefined") return;
  if (scrollY == null && !hasCapturedScroll) return;
  const y = scrollY ?? capturedScrollY;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => writeScrollY(y));
  });
}

export function withScrollPreservedStateUpdate(fn) {
  return (...args) => {
    capturePageScrollY();
    fn(...args);
    restorePageScrollAfterPaint();
  };
}

function captureMenuScroll() {
  capturePageScrollY();
}

const MENU_SCROLL_PROPS_BASE = {
  disableScrollLock: true,
  disableAutoFocusItem: true,
  disableRestoreFocus: true,
  autoFocus: false,
  MenuListProps: {
    autoFocus: false,
    autoFocusItem: false,
  },
};

/** MUI Menu — açılış/kapanışta sayfa kaydırmasını önler */
export function buildMenuScrollProps(overrides = {}) {
  const {
    onClose,
    PaperProps,
    MenuListProps,
    TransitionProps,
    ...rest
  } = overrides;

  return {
    ...MENU_SCROLL_PROPS_BASE,
    ...rest,
    ...(typeof onClose === "function"
      ? {
          onClose: (...args) => {
            onClose(...args);
          },
        }
      : {}),
    TransitionProps: {
      ...TransitionProps,
    },
    MenuListProps: {
      ...MENU_SCROLL_PROPS_BASE.MenuListProps,
      ...MenuListProps,
    },
    ...(PaperProps ? { PaperProps } : {}),
  };
}

/** MUI Select — menü açılış/kapanışında scroll sıçramasını önler (stabil handler referansları) */
const SELECT_SCROLL_ON_OPEN = captureMenuScroll;

export function buildSelectScrollProps() {
  return {
    onOpen: SELECT_SCROLL_ON_OPEN,
    MenuProps: buildMenuScrollProps(),
  };
}

/** Select odaklandığında tarayıcının scrollIntoView ile sayfayı zıplatmasını engeller */
export function preventFocusScroll(event) {
  const target = event?.target;
  if (target && typeof target.scrollIntoView === "function") {
    target.scrollIntoView = () => {};
  }
}

/** Select tıklamasında odak kaymasını engeller; menü yine açılır */
export function preventSelectMouseDownScroll(event) {
  event.preventDefault();
}

/** Çoklu Select: seçim/kaldırma sırasında menüyü açık tut; dışarı tıklayınca kapat */
export function createKeepOpenMenuCloseHandler(setMenuOpen, options = {}) {
  const { onClosed, getScrollY } = options;
  return createFilterMenuCloseHandler(setMenuOpen, {
    onClosed,
    getScrollY,
    keepOpenUntilDismiss: true,
  });
}

/** Tekli / çoklu filtre Select — backdrop ve Escape ile kapanır */
export function createFilterMenuCloseHandler(setMenuOpen, options = {}) {
  const {
    onClosed,
    keepOpenReasons = new Set(),
    keepOpenUntilDismiss = false,
    blurOnClose = true,
    getScrollY,
  } = options;

  return (event, reason) => {
    if (keepOpenUntilDismiss) {
      if (!FILTER_MENU_DISMISS_REASONS.has(reason)) {
        return;
      }
    } else if (keepOpenReasons.has(reason)) {
      return;
    }

    const scrollY = typeof getScrollY === "function" ? getScrollY() : capturedScrollY;
    restorePageScrollAfterPaint(scrollY);
    setMenuOpen(false);
    if (blurOnClose) {
      blurActiveFilterSelect();
    }
    onClosed?.(event, reason);
  };
}

/** Menü backdrop ile kapanınca Select odakta kalmasın (etiket rengi düzelsin) */
export function blurActiveFilterSelect() {
  requestAnimationFrame(() => {
    const el = document.activeElement;
    if (!el || el === document.body || el === document.documentElement) {
      return;
    }
    const isSelectControl =
      el.classList.contains("MuiSelect-select") ||
      el.classList.contains("MuiOutlinedInput-input") ||
      el.closest(".MuiSelect-root");
    if (isSelectControl && typeof el.blur === "function") {
      el.blur();
    }
  });
}

/** MenuItem tıklamasında odak kaymasını ve gereksiz kapanmayı azaltır */
export function preventMenuItemFocusScroll(event) {
  event.preventDefault();
}

/** MUI Dialog / Modal — body scroll kilidi sayfa konumunu zıplatmasın */
export const DIALOG_SCROLL_PROPS_BASE = {
  disableScrollLock: true,
};

/** Dialog bileşenlerine güvenli scroll props (theme default ile uyumlu) */
export function buildDialogScrollProps(overrides = {}) {
  const { TransitionProps, ...rest } = overrides;
  return {
    ...DIALOG_SCROLL_PROPS_BASE,
    ...rest,
    TransitionProps: buildDialogScrollPreservingTransitionProps(TransitionProps),
  };
}

/** Dialog açılış animasyonunda scroll konumunu korur */
export function buildDialogScrollPreservingTransitionProps(userTransitionProps = {}) {
  const { onEnter, onEntered, ...rest } = userTransitionProps;
  return {
    ...rest,
    onEnter: (...args) => {
      capturePageScrollY();
      onEnter?.(...args);
    },
    onEntered: (...args) => {
      restorePageScrollAfterPaint();
      onEntered?.(...args);
    },
  };
}

/** Dialog içindeki ilk alana odaklanırken sayfayı kaydırmaz */
export function focusWithoutScroll(element) {
  if (!element || typeof element.focus !== "function") return;
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}
